import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IAttrValue } from "@multiweb/types/contracts";
import { Column } from "UI/UI.Controls/DataGridSubcontrols/Columns/Column";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../../../../Global/Base/Rights/RightTypes";
import { AttrValueReader } from "../../../../../../../OW.Remoting/Remoting/AttrValueReader";
import { EventHelper } from "../../../../../../UI.BaseControls/Helpers/EventHelper";
import { EditItemTemplate } from "../../../../../../UI.Controls/DataGridSubcontrols/Templates/EditItemTemplate";
import { ElementUtils } from "../../../../../../UI.ElementarControls/Helpers/ElementUtils";
import { ContainerEditableControl } from "../../../../Controls/ContainerEditableControl";
import { FormControlParams, IEditableControl, IHTMLElementWithControl } from "../../../../Controls/ControlInterfaces";

const logger = LazyLoadingSectionLogger.create(LoggingSection.dataGridColumn);

/**
* Základní template pro komponenty založené na custom controlech používající wrapper ContainerEditableControl
*/
export class DefaultEditContainerTemplate extends EditItemTemplate {
    protected _containerCreator: (params: FormControlParams) => ContainerEditableControl;

    /**
     * Požaduje tovární metodu vytvářející ContainerEditableControl
     * @param containerCreator
     */
    constructor(containerCreator: (params: FormControlParams) => ContainerEditableControl) {
        super();
        this._containerCreator = containerCreator;
    }

    /**
     * Vytvoří a ziniciuje novou komponentu a její kontejner
     * @param dataSet Data k zobrazení v komponentě
     * @param parentItem Rodičovský element, do kterého se vloží nový element
     * @param column Definice sloupce
     * @param controlElement Container element s definicí komponenty, pokud není zadán, vytvoří se prázdný div
     */
    protected _createEditContainerElement(dataSet: IAttrValue[], parentItem: HTMLElement, column: Column, controlElement?: HTMLElement): ContainerEditableControl {
        const containerControl = this._containerCreator({
            element: controlElement ?? document.createElement("div"),
            ownerForm: undefined,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
        });
        containerControl.enabledVisual = false; //předejde se tak možnosti editovat hodnotu dříve než to povolí práva
        containerControl.attrName = column.attrName;

        containerControl.element.addEventListener("click", (e: MouseEvent) => EventHelper.stopPropagation(e));

        containerControl.controlValue = AttrValueReader.read(this._getDataFromSet(dataSet));
        containerControl.valueChanged.add(() => {
            logger.log(`Element '${containerControl.innerElement.tagName}' is changed`);
            ElementUtils.callEvent(parentItem, "valuechanged", { value: containerControl.controlValue, column: this.parentColumn });
        });
        containerControl.isEditing = true;

        return containerControl;
    }

    public override updateData(dataSet: IAttrValue[], item: HTMLElement): void {
        const inputElement = this._getInputElement(item);
        if (inputElement) {
            inputElement.value = AttrValueReader.read(this._getDataFromSet(dataSet));
        }
    }

    protected _getInputElement(item: HTMLElement): HTMLInputElement | null {
        if (item instanceof HTMLInputElement) {
            return item;
        }
        if (!(item instanceof IHTMLElementWithControl) || !(item.tscControl instanceof IEditableControl)) {
            return null;
        }
        const innerElement = item.tscControl.innerElement;
        if (innerElement instanceof HTMLInputElement) {
            return innerElement;
        }
        return null;
    }
}
